import { useOutletContext, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Tenant } from '@/lib/tenant';
import { fetchServices } from '@/lib/catalog';
import { fetchTenantBusiness } from '@/lib/tenantBusiness';
import { defaultSalonSettings } from '@/lib/salonSettings';
import {
  ArrowRight,
  Clock,
  MapPin,
  NavigationArrow,
  Phone,
  Sparkle
} from '@phosphor-icons/react';

// Витрина салона: сразу сайт, без рекламных вставок и объяснений.
// Hero с адресом и записью, галерея работ, услуги, о салоне.
// Фото лежат в корне сайта: /gallery/01.jpg. Никаких /s/<slug>/.
// Контент берём из настроек салона: владелец правит их в кабинете,
// витрина подхватывает правки без пересборки.
export function ClientHome() {
  const { tenant } = useOutletContext<{ tenant: Tenant }>();

  const { data: services, isLoading, isError } = useQuery({
    queryKey: ['services', tenant.id],
    queryFn: () => fetchServices(tenant.id)
  });

  // Правки владельца: имя, контакты, о салоне, галерея.
  // Прод: данные из tenants.business (D1). Демо: из localStorage.
  const { data: settings = defaultSalonSettings() } = useQuery({
    queryKey: ['salon-settings', tenant.id],
    queryFn: () => fetchTenantBusiness(tenant.id)
  });
  const b = tenant.business;
  const gallery = settings.gallery.length > 0 ? settings.gallery : [];

  const name = settings.name || tenant.name;
  const tagline = settings.tagline || b.tagline;
  const address = settings.address || tenant.address;
  const addressHint = settings.addressHint || b.addressHint;
  const phone = settings.phone || tenant.phone;
  const mapUrl = settings.mapUrl || b.mapUrl;
  const about = settings.about || b.about;
  const todayKey = new Intl.DateTimeFormat('en-US', {
    timeZone: tenant.timezone,
    weekday: 'short'
  }).format(new Date()).toLowerCase() as keyof typeof settings.schedule;
  const todayHours = settings.schedule[todayKey] ?? null;

  const heroPhoto = gallery[0];

  return (
    <main className="mnc-page mnc-home">
      <header className="mnc-homebar">
        <Link className="mnc-wordmark" to="/" aria-label={`${name}, главная`}>
          <span>{name.slice(0, 1)}</span>
          <b>{name}</b>
        </Link>
        <span className="mnc-online-pill"><i /> Онлайн-запись</span>
      </header>

      <section className={`mnc-hero${heroPhoto ? ' mnc-hero-with-photo' : ''}`}>
        <div className="mnc-hero-copy">
          <span className="mnc-kicker"><Sparkle weight="fill" /> Студия красоты</span>
          <h1 className="mnc-salon-title">{name}</h1>
          {tagline && <p className="mnc-lead">{tagline}</p>}

          <div className="mnc-actions">
            <Link className="mnc-btn" to="/book">
              Выбрать время <ArrowRight weight="bold" />
            </Link>
          </div>
        </div>

        {heroPhoto && (
          <div className="mnc-hero-photo">
            <img src={heroPhoto.src} alt={heroPhoto.caption} />
            <span><Sparkle weight="fill" /> {heroPhoto.caption}</span>
          </div>
        )}
      </section>

      <section className="mnc-contact-strip" aria-label="Контакты салона">
        {address && (
          <div className="mnc-contact-item">
            <span className="mnc-icon-tile"><MapPin weight="duotone" /></span>
            <span><small>Адрес</small><b>{address}</b>{addressHint && <em>{addressHint}</em>}</span>
          </div>
        )}
        {phone && (
          <a className="mnc-contact-item" href={`tel:${phone.replace(/[^+\d]/g, '')}`}>
            <span className="mnc-icon-tile"><Phone weight="duotone" /></span>
            <span><small>Телефон</small><b>{phone}</b><em>Позвонить в салон</em></span>
          </a>
        )}
        <div className="mnc-contact-item">
          <span className="mnc-icon-tile"><Clock weight="duotone" /></span>
          <span>
            <small>Сегодня</small>
            <b>{todayHours ? `${todayHours.open}–${todayHours.close}` : 'Выходной'}</b>
            <em>{todayHours ? 'Работаем по записи' : 'Выберите другой день'}</em>
          </span>
        </div>
        {mapUrl && (
          <a className="mnc-contact-map" href={mapUrl} target="_blank" rel="noreferrer" aria-label="Открыть карту">
            <NavigationArrow weight="fill" />
          </a>
        )}
      </section>

      <section className="mnc-gallery-block">
        <div className="mnc-section-heading">
          <div><span className="mnc-eyebrow">Портфолио</span><h2>Наши работы</h2></div>
          <span>{gallery.length} фото</span>
        </div>
        {gallery.length === 0 ? (
          <p className="mnc-muted">Фотографии работ появятся здесь.</p>
        ) : (
          <div className="mnc-gallery">
            {gallery.map((g) => (
              <figure className="mnc-shot" key={g.src}>
                <img src={g.src} alt={g.caption} loading="lazy" />
                <figcaption>{g.caption}</figcaption>
              </figure>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="mnc-section-heading">
          <div><span className="mnc-eyebrow">Прайс</span><h2>Услуги</h2></div>
          <Link to="/book">Записаться <ArrowRight /></Link>
        </div>
        {isLoading && <p className="mnc-muted">Загружаем услуги…</p>}
        {isError && <p className="mnc-muted">Не удалось загрузить услуги.</p>}
        <div className="mnc-service-list">
          {services?.map((s) => (
            <article className="mnc-card" key={s.id}>
              <span className="mnc-service-icon"><Sparkle weight="duotone" /></span>
              <div className="mnc-service-copy">
                <div className="mnc-card-head">
                  <h3>{s.name}</h3>
                  <span className="mnc-price">{s.price} ₽</span>
                </div>
                {s.description && <p className="mnc-muted">{s.description}</p>}
                <span className="mnc-duration"><Clock /> {s.duration_min} мин</span>
              </div>
              {s.popular && <span className="mnc-tag">Часто выбирают</span>}
              <Link className="mnc-card-arrow" to="/book" aria-label={`Записаться на ${s.name}`}><ArrowRight /></Link>
            </article>
          ))}
        </div>
      </section>

      {about && (
        <section className="mnc-about-card">
          <span className="mnc-eyebrow">О нас</span>
          <h2>Место, куда хочется возвращаться</h2>
          <p>{about}</p>
          <Link className="mnc-text-link" to="/book">Подобрать удобное время <ArrowRight /></Link>
        </section>
      )}

      <footer className="mnc-footer">
        {name}
        {address ? ` · ${address}` : ''}
      </footer>

    </main>
  );
}
